import { formatIndianDate, isoToDmy, dmyToIso } from "@/lib/date";
import MemberPicker from "../MemberPicker";

// Column keys that should render as a MemberPicker dropdown when the caller
// supplies a `members` roster. Used by MGT-1 transaction add forms and SH-6
// table-mode entries so users can pick from existing members + fall back to
// free text for external / inter-company transfers.
const MEMBER_PICKER_KEYS = new Set([
  "name_of_transferor",
  "name_of_transferee",
  "transferor_name_address",
  "transferee_name_address",
]);

// Companion folio keys auto-filled when a roster member is picked.
const COMPANION_FOLIO = {
  name_of_transferor: "folio_of_transferor",
  name_of_transferee: "folio_of_transferee",
  transferor_name_address: "transferor_folio",
  transferee_name_address: "transferee_folio",
};

/** Format a single table cell based on column type. Used by table-mode rows and folio detail views. */
export function formatCell(type, value) {
  if (value === undefined || value === null || value === "") {
    return <span className="text-slate-300">—</span>;
  }
  if (type === "date") return formatIndianDate(value);
  if (type === "number") {
    try {
      const n = Number(value);
      return n.toLocaleString("en-IN");
    } catch (err) {
      // Fall through to raw string render on unexpected toLocaleString failure
      console.warn("formatCell: toLocaleString failed for", value, err);
    }
  }
  return String(value);
}

/** Renders one input per column, normalising date inputs to ISO on blur.
 *  Optional `members` enables MemberPicker on transferor/transferee name
 *  columns for MGT-1 / SH-6 entry forms.
 */
export function EntryForm({ columns, value, onChange, members = [] }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const setMany = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {columns.map((c) => {
        const raw = value?.[c.key] ?? "";
        if (members.length > 0 && MEMBER_PICKER_KEYS.has(c.key)) {
          const folioKey = COMPANION_FOLIO[c.key];
          return (
            <label key={c.key} className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</span>
              <div className="mt-1">
                <MemberPicker
                  members={members}
                  value={raw}
                  onChange={(v) => set(c.key, v)}
                  onPickMember={(m) => {
                    if (m && folioKey) {
                      const patch = { [c.key]: m.name || "" };
                      patch[folioKey] = m.folio_no || "";
                      setMany(patch);
                    }
                  }}
                  testId={`entry-field-${c.key}`}
                />
              </div>
            </label>
          );
        }
        if (c.type === "select" && Array.isArray(c.options)) {
          return (
            <label key={c.key} className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</span>
              <select
                value={raw}
                onChange={(e) => set(c.key, e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm mt-1 bg-white focus:outline-none focus:border-blue-700"
                data-testid={`entry-field-${c.key}`}
              >
                <option value="">— Select —</option>
                {c.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
          );
        }
        if (c.type === "textarea") {
          return (
            <label key={c.key} className="block md:col-span-2">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</span>
              <textarea
                value={raw}
                onChange={(e) => set(c.key, e.target.value)}
                rows={2}
                className="w-full border border-slate-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:border-blue-700"
                data-testid={`entry-field-${c.key}`}
              />
            </label>
          );
        }
        if (c.type === "date") {
          return (
            <label key={c.key} className="block">
              <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</span>
              <input
                value={isoToDmy(raw)}
                onChange={(e) => set(c.key, e.target.value)}
                onBlur={(e) => {
                  const iso = dmyToIso(e.target.value);
                  if (iso || !e.target.value) set(c.key, iso);
                }}
                placeholder="DD-MM-YYYY"
                inputMode="numeric"
                className="w-full border border-slate-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:border-blue-700"
                data-testid={`entry-field-${c.key}`}
              />
            </label>
          );
        }
        return (
          <label key={c.key} className="block">
            <span className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</span>
            <input
              type={c.type === "number" ? "number" : "text"}
              value={raw}
              onChange={(e) => set(c.key, e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:border-blue-700"
              data-testid={`entry-field-${c.key}`}
            />
          </label>
        );
      })}
    </div>
  );
}
